/**
 * Pure planning for "the user typed a message while an approval was pending":
 * which approval takes the user's words, and what verdict each one gets.
 */
import { ASK_USER_APPROVAL_KIND } from '../../../contracts/ask-user-details.types';
import type {
  PendingToolApproval,
  ToolApprovalVerdict,
} from '../../../contracts/tool-approval.types';
import { TOOL_APPROVAL_REASON_MAX_LENGTH } from '../../../contracts/tool-approval.types';
import {
  buildClarificationModifiedInput,
  clarificationAnswersComplete,
  nextUnansweredQuestion,
  parseQuestions,
} from '../../harness/ask-user-details';
import type { ClarificationAnswerDraft } from '../state/clarificationAnswerDraftAtom';
import type { SubmitProjectTurnArgs } from '../submit-project-turn-args';

/**
 * Rides every pending approval EXCEPT the one taking the user's own words. It is
 * a rejection notice, never an answer, so it only ever rides a `deny`: approving
 * a free-text clarification with it would hand the model this canned sentence as
 * the user's genuine reply.
 */
export const SUPERSEDED_APPROVAL_REASON = "Superseded by the user's new instruction.";

export interface ApprovalSteerStep {
  approval: PendingToolApproval;
  verdict: ToolApprovalVerdict;
  reason: string;
  modifiedInput: Record<string, unknown> | null;
  /** Persist the draft and skip POST — more answers still needed. */
  deferResolve?: boolean;
  nextDraft?: ClarificationAnswerDraft;
}

/**
 * ``reason`` is capped at 500 chars backend-side as a deliberate PHI bound. A
 * longer draft is never truncated — the caller falls back to cancel-and-resend
 * so the user's words survive in full.
 */
export const canSteerWithText = (text: string): boolean =>
  text.length > 0 && text.length <= TOOL_APPROVAL_REASON_MAX_LENGTH;

/**
 * The ``Pick`` list is the explicit record of which payload fields a 500-char
 * ``reason`` can carry. A turn carrying anything outside it (``notePushPlan``,
 * ``analytics``, …) must take the cancel-and-resend fallback.
 */
export type SteerableComposerTurn = Pick<
  SubmitProjectTurnArgs,
  'text' | 'displayText' | 'files' | 'attachment_ids' | 'computerUseSkill'
> & { readonly linkedItems: readonly unknown[] };

/**
 * ``null`` when the draft carries something a ``reason`` string cannot — which
 * sends the caller down the cancel-and-resend fallback.
 */
export const steerTextForTurn = (turn: SteerableComposerTurn): string | null => {
  if (turn.displayText !== undefined) return null;
  if (turn.computerUseSkill) return null;
  if (turn.files && turn.files.length > 0) return null;
  if (turn.attachment_ids && turn.attachment_ids.length > 0) return null;
  if (turn.linkedItems.length > 0) return null;
  const text = turn.text.trim();
  return canSteerWithText(text) ? text : null;
};

/** Presented approval first — the one the user was looking at — then oldest-first. */
export const orderApprovalsForSteer = (
  approvals: readonly PendingToolApproval[],
  presentedApprovalId?: string
): PendingToolApproval[] => {
  const byAge = [...approvals].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  if (!presentedApprovalId) return byAge;
  const presentedIndex = byAge.findIndex(
    (approval) => approval.approvalId === presentedApprovalId
  );
  if (presentedIndex <= 0) return byAge;
  const [presented] = byAge.splice(presentedIndex, 1);
  return presented ? [presented, ...byAge] : byAge;
};

const denyWithReason = (
  approval: PendingToolApproval,
  text: string
): ApprovalSteerStep => ({
  approval,
  verdict: 'deny',
  reason: text,
  modifiedInput: null,
});

/**
 * A typed reply to a folded ``kind=text`` question is an ANSWER, not a
 * rejection — but only while that text question is the EARLIEST
 * unanswered one in the agent's order. A composer reply must not be
 * allowed to jump ahead and silently answer a LATER text question while
 * an earlier structured question is still outstanding — that structured
 * question's card is on screen precisely so the user answers it there,
 * and stashing a text-shaped answer for a question further down the
 * batch would let the earlier one sit unresolved with no signal anything
 * is wrong. Sequential text (and mixed batches still missing a text
 * answer that IS next) stash or resolve. Everything else, including a
 * choice card with no unanswered text due next, denies with the text as
 * the reason.
 */
const planPrimaryStep = (
  approval: PendingToolApproval,
  text: string,
  draft: ClarificationAnswerDraft
): ApprovalSteerStep => {
  if (approval.approvalKind !== ASK_USER_APPROVAL_KIND) {
    return denyWithReason(approval, text);
  }
  const questions = parseQuestions(approval.toolInput);
  if (!questions) return denyWithReason(approval, text);
  const next = nextUnansweredQuestion(questions, draft);
  if (next?.kind !== 'text') return denyWithReason(approval, text);
  const nextDraft: ClarificationAnswerDraft = {
    ...draft,
    [next.id]: { selected_option_ids: [], text },
  };
  if (!clarificationAnswersComplete(questions, nextDraft)) {
    return {
      approval,
      verdict: 'approve',
      reason: '',
      modifiedInput: null,
      deferResolve: true,
      nextDraft,
    };
  }
  return {
    approval,
    verdict: 'approve',
    reason: '',
    modifiedInput: buildClarificationModifiedInput(questions, nextDraft),
    nextDraft,
  };
};

const supersededStep = (approval: PendingToolApproval): ApprovalSteerStep => ({
  approval,
  verdict: 'deny',
  reason: SUPERSEDED_APPROVAL_REASON,
  modifiedInput: null,
});

export interface PlanApprovalSteerArgs {
  approvals: readonly PendingToolApproval[];
  presentedApprovalId?: string;
  /** Already trimmed and length-checked by the caller. */
  text: string;
  /** Stashed answers for the presented (primary) approval. */
  draft?: ClarificationAnswerDraft;
}

/**
 * One resolve per pending approval, not just the presented one: parallel tool
 * calls each hold their own durable wait and the turn only resumes once all are
 * satisfied, so leaving one pending re-strands it. Only the first step may
 * answer; the rest are superseded denials. A deferred primary (more answers
 * still needed) returns that step alone so siblings stay pending.
 */
export const planApprovalSteer = ({
  approvals,
  presentedApprovalId,
  text,
  draft = {},
}: PlanApprovalSteerArgs): ApprovalSteerStep[] => {
  const ordered = orderApprovalsForSteer(approvals, presentedApprovalId);
  const primary = ordered[0];
  if (!primary) return [];
  const primaryStep = planPrimaryStep(primary, text, draft);
  if (primaryStep.deferResolve) return [primaryStep];
  return [primaryStep, ...ordered.slice(1).map(supersededStep)];
};
